/* oxlint-disable typescript/no-unsafe-argument */
import { useCallback, useEffect, useState } from 'react'
import { useLocalStorageContext } from '../local-storage-ctx'
import { storageChangeEvent } from '../in-memory-local-storage'

export function useLocalStorage<T>(
  key: string,
  defaultValue: T,
): [T, (value: T | ((prev: T) => T)) => void, () => void] {
  const storage = useLocalStorageContext()

  return useStorage(storage, key, defaultValue)
}

export function useStorage<T>(
  storage: Storage,
  key: string,
  defaultValue: T,
): [T, (value: T | ((prev: T) => T)) => void, () => void] {
  const readValue = useCallback((): T => {
    try {
      const item = storage.getItem(key)
      return item ? (JSON.parse(item) as T) : defaultValue
    } catch {
      return defaultValue
    }
  }, [storage, key, defaultValue])

  const [value, setValueState] = useState<T>(readValue)

  const setValue = useCallback(
    (newValue: T | ((prev: T) => T)) => {
      const resolved =
        typeof newValue === 'function'
          ? (newValue as (prev: T) => T)(value)
          : newValue
      setValueState(resolved)
      storage.setItem(key, JSON.stringify(resolved))
    },
    [storage, key, value],
  )

  const remove = useCallback(() => {
    setValueState(defaultValue)
    storage.removeItem(key)
  }, [storage, key, defaultValue])

  // Listen to our custom storage change event (when we set value outside of this hook)
  storageChangeEvent.useListen((e) => {
    if (e.key === key && e.storage === storage) {
      setValueState(e.newValue ? JSON.parse(e.newValue) : defaultValue)
    }
  })

  // Sync across tabs (for real localStorage)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.storageArea === storage) {
        setValueState(e.newValue ? JSON.parse(e.newValue) : defaultValue)
      }
    }
    window.addEventListener('storage', handleStorageChange)
    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [key, storage, defaultValue])

  return [value, setValue, remove]
}
